import React, { useState, useMemo, useCallback } from 'react'
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react'

export type SortDirection = 'asc' | 'desc' | null

export interface Column<T> {
  key: string
  header: string
  accessor?: (row: T) => any
  render?: (value: any, row: T, index: number) => React.ReactNode
  sortable?: boolean
  sortType?: 'text' | 'number' | 'date' | 'price' | 'status'
  width?: string
  className?: string
  headerClassName?: string
}

interface DataTableProps<T> {
  columns: Column<T>[]
  data: T[]
  loading?: boolean
  emptyMessage?: string
  onRowClick?: (row: T, index: number) => void
  rowClassName?: (row: T, index: number) => string
  className?: string
  stickyHeader?: boolean
}

const sortComparators = {
  text: (a: any, b: any) => String(a || '').localeCompare(String(b || ''), undefined, { numeric: false, sensitivity: 'base' }),
  number: (a: any, b: any) => (Number(a) || 0) - (Number(b) || 0),
  date: (a: any, b: any) => {
    const dateA = new Date(a).getTime()
    const dateB = new Date(b).getTime()
    return dateA - dateB
  },
  price: (a: any, b: any) => (Number(a) || 0) - (Number(b) || 0),
  status: (a: any, b: any) => String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base' }),
}

export default function DataTable<T extends { id?: string | number }>({
  columns,
  data,
  loading = false,
  emptyMessage = 'No data found',
  onRowClick,
  rowClassName,
  className = '',
  stickyHeader = true,
}: DataTableProps<T>) {
  const [sortBy, setSortBy] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>(null)

  const handleSort = useCallback((key: string, sortType: string = 'text') => {
    if (sortBy === key) {
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else if (sortDirection === 'desc') {
        setSortDirection(null)
        setSortBy(null)
      }
    } else {
      setSortBy(key)
      setSortDirection('asc')
    }
  }, [sortBy, sortDirection])

  const sortedData = useMemo(() => {
    if (!sortBy || !sortDirection) return data

    const column = columns.find(col => col.key === sortBy)
    if (!column) return data

    const comparator = sortComparators[column.sortType as keyof typeof sortComparators] || sortComparators.text

    const sorted = [...data].sort((a, b) => {
      const aValue = column.accessor ? column.accessor(a) : a[sortBy as keyof T]
      const bValue = column.accessor ? column.accessor(b) : b[sortBy as keyof T]

      const comparison = comparator(aValue, bValue)
      return sortDirection === 'asc' ? comparison : -comparison
    })

    return sorted
  }, [data, sortBy, sortDirection, columns])

  const getSortIcon = (columnKey: string) => {
    if (sortBy !== columnKey) {
      return <ChevronsUpDown size={16} className="text-gray-400" />
    }
    if (sortDirection === 'asc') {
      return <ChevronUp size={16} className="text-[#6D3A0D]" />
    }
    return <ChevronDown size={16} className="text-[#6D3A0D]" />
  }

  return (
    <div className={`overflow-x-auto rounded-lg border border-gray-200 ${className}`}>
      <table className="w-full">
        <thead>
          <tr className={`border-b border-gray-200 bg-gray-50 ${stickyHeader ? 'sticky top-0 z-10' : ''}`}>
            {columns.map((col) => (
              <th
                key={col.key}
                onClick={() => col.sortable && handleSort(col.key, col.sortType || 'text')}
                className={`
                  px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-900 whitespace-nowrap
                  ${col.width ? col.width : ''}
                  ${col.headerClassName || ''}
                  ${col.sortable ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''}
                `}
              >
                <div className="flex items-center gap-2">
                  <span>{col.header}</span>
                  {col.sortable && getSortIcon(col.key)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={columns.length} className="px-3 sm:px-6 py-8 text-center text-gray-500">
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#6D3A0D]"></div>
                  Loading...
                </div>
              </td>
            </tr>
          ) : sortedData.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-3 sm:px-6 py-8 text-center text-gray-500">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            sortedData.map((row, idx) => (
              <tr
                key={row.id || idx}
                onClick={() => onRowClick?.(row, idx)}
                className={`
                  border-b border-gray-100 hover:bg-gray-50 transition-colors
                  ${onRowClick ? 'cursor-pointer' : ''}
                  ${rowClassName?.(row, idx) || ''}
                `}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`px-3 sm:px-6 py-4 text-xs sm:text-sm text-gray-700 ${col.className || ''}`}
                  >
                    {col.render
                      ? col.render(
                          col.accessor ? col.accessor(row) : row[col.key as keyof T],
                          row,
                          idx
                        )
                      : col.accessor
                        ? col.accessor(row)
                        : row[col.key as keyof T]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}
